import type { FormEvent } from 'react';
import { useEffect, useState } from 'react';
import { searchBrandSources, type BrandKeywordResult, type BrandSearchSource } from '../../../../shared/brand-chat/keywordSearch';

type Props = { brandName: string; siteUrl: string };
type Language = 'zh-TW' | 'zh-CN' | 'en' | 'ja' | 'ko';
type Mode = 'keyword' | 'ai';
const MAX_CACHED_PAGE_COUNT = 12;
const MAX_CACHED_PAGE_LENGTH = 12000;

const ui: Record<Language, Record<string, string>> = {
  'zh-TW': {
    title: 'AI 客服（Beta）', subtitle: '搜尋品牌網站公開資訊', placeholder: '想查什麼？',
    start: '可搜尋書名、作者與網站公開資訊。需要自然語言理解時，切換「AI 問答」。',
    keywordMode: '關鍵字搜尋', aiMode: 'AI 問答', loading: 'AI 正在整理網站資訊…',
    sources: '參考頁面', open: '開啟 AI 客服（Beta）', close: '關閉 AI 客服', send: '送出問題',
    noResults: '目前找不到符合的站內資料。試試書名、作者或較短的關鍵字。',
    aiError: 'AI 問答暫時無法使用，以下改顯示站內關鍵字搜尋結果。',
    searchResults: '站內搜尋結果',
  },
  'zh-CN': {
    title: 'AI 客服（Beta）', subtitle: '搜索品牌网站公开信息', placeholder: '想查什么？',
    start: '可搜索书名、作者与网站公开信息。需要自然语言理解时，切换「AI 问答」。',
    keywordMode: '关键词搜索', aiMode: 'AI 问答', loading: 'AI 正在整理网站信息…',
    sources: '参考页面', open: '打开 AI 客服（Beta）', close: '关闭 AI 客服', send: '发送问题',
    noResults: '目前找不到符合的站内资料。试试书名、作者或较短的关键词。',
    aiError: 'AI 问答暂时无法使用，以下改为显示站内关键词搜索结果。',
    searchResults: '站内搜索结果',
  },
  en: {
    title: 'AI customer service (Beta)', subtitle: 'Search public information on this brand site',
    placeholder: 'What would you like to find?',
    start: 'Search book titles, authors, and public site information. Switch to AI Q&A when you need natural language understanding.',
    keywordMode: 'Keyword search', aiMode: 'AI Q&A', loading: 'AI is reviewing the site information…',
    sources: 'Reference pages', open: 'Open AI customer service (Beta)', close: 'Close AI customer service',
    send: 'Send question', noResults: 'No matching pages found. Try a book title, author, or shorter keyword.',
    aiError: 'AI Q&A is temporarily unavailable. Here are the site keyword results instead.',
    searchResults: 'Site search results',
  },
  ja: {
    title: 'AI カスタマーサービス（Beta）', subtitle: 'ブランドサイトの公開情報を検索',
    placeholder: '何をお探しですか？',
    start: '書名、著者、サイトの公開情報を検索できます。自然な文章で質問する場合は「AI 質問回答」に切り替えてください。',
    keywordMode: 'キーワード検索', aiMode: 'AI 質問回答', loading: 'AI がサイト情報を確認しています…',
    sources: '参考ページ', open: 'AI カスタマーサービスを開く', close: '閉じる', send: '質問を送信',
    noResults: '一致するページがありません。書名、著者、短いキーワードをお試しください。',
    aiError: 'AI 質問回答は一時的に利用できません。サイト内検索結果を表示します。',
    searchResults: 'サイト内検索結果',
  },
  ko: {
    title: 'AI 고객 서비스 (Beta)', subtitle: '브랜드 사이트 공개 정보 검색',
    placeholder: '무엇을 찾으시나요?',
    start: '책 제목, 저자와 사이트 공개 정보를 검색할 수 있습니다. 자연어 질문은 AI Q&A로 전환하세요.',
    keywordMode: '키워드 검색', aiMode: 'AI Q&A', loading: 'AI가 사이트 정보를 확인하고 있습니다…',
    sources: '참고 페이지', open: 'AI 고객 서비스 열기', close: '닫기', send: '질문 보내기',
    noResults: '일치하는 페이지가 없습니다. 책 제목, 저자 또는 짧은 키워드를 입력해 보세요.',
    aiError: 'AI Q&A를 잠시 사용할 수 없습니다. 사이트 검색 결과를 표시합니다.',
    searchResults: '사이트 검색 결과',
  },
};

function detectLanguage(value: string, fallback = 'zh-TW'): Language {
  if (/[\u3040-\u30ff]/u.test(value)) return 'ja';
  if (/[\uac00-\ud7af\u1100-\u11ff\u3130-\u318f]/u.test(value)) return 'ko';
  if (/[简体书这为与发后]/u.test(value)) return 'zh-CN';
  const latin = (value.match(/[A-Za-z]/gu) || []).length;
  const han = (value.match(/[\u3400-\u4dbf\u4e00-\u9fff]/gu) || []).length;
  if (latin >= 3 && (han === 0 || latin >= han * 2)) return 'en';
  const lower = fallback.toLowerCase();
  if (lower.startsWith('en')) return 'en';
  if (lower.startsWith('ja')) return 'ja';
  if (lower.startsWith('ko')) return 'ko';
  if (lower.startsWith('zh-cn')) return 'zh-CN';
  return 'zh-TW';
}

function contextStorageKey(siteUrl: string): string {
  try {
    return `sonbook-ai-context:${new URL(siteUrl).hostname}`;
  } catch {
    return 'sonbook-ai-context:unknown';
  }
}

function readCachedPages(siteUrl: string): BrandSearchSource[] {
  try {
    const value = JSON.parse(sessionStorage.getItem(contextStorageKey(siteUrl)) || '[]');
    return Array.isArray(value)
      ? value.filter((item): item is BrandSearchSource => Boolean(item?.href && item?.excerpt)).slice(0, MAX_CACHED_PAGE_COUNT)
      : [];
  } catch {
    return [];
  }
}

function rememberCurrentPage(brandName: string, siteUrl: string): BrandSearchSource[] {
  const body = document.body?.innerText?.replace(/\s+/g, ' ').trim() || '';
  if (!body) return readCachedPages(siteUrl);
  const current: BrandSearchSource = {
    title: document.title || brandName,
    excerpt: body.slice(0, MAX_CACHED_PAGE_LENGTH),
    href: window.location.href,
  };
  const pages = [current, ...readCachedPages(siteUrl)].filter((item, index, items) =>
    items.findIndex((candidate) => candidate.href === item.href) === index,
  ).slice(0, MAX_CACHED_PAGE_COUNT);
  try {
    sessionStorage.setItem(contextStorageKey(siteUrl), JSON.stringify(pages));
  } catch {
    // The current page remains searchable when browser storage is unavailable.
  }
  return pages;
}

function collectSources(brandName: string, siteUrl: string): BrandSearchSource[] {
  const body = document.body?.innerText?.replace(/\s+/g, ' ').trim() || '';
  const sources: BrandSearchSource[] = [
    { title: brandName, excerpt: `${brandName} official website ${siteUrl}`, href: siteUrl },
    ...rememberCurrentPage(brandName, siteUrl),
  ];
  if (body) sources.push({ title: document.title || brandName, excerpt: body.slice(0, MAX_CACHED_PAGE_LENGTH), href: window.location.href });

  const uniqueSources = sources.filter((source, index, items) =>
    items.findIndex((candidate) => candidate.href === source.href) === index,
  );
  const seen = new Set(uniqueSources.map((item) => item.href));
  Array.from(document.querySelectorAll<HTMLAnchorElement>('a[href]'))
    .map((link) => ({ title: link.textContent?.replace(/\s+/g, ' ').trim() || link.getAttribute('aria-label') || '', href: link.href }))
    .filter((link) => link.title && link.href.startsWith(window.location.origin) && !seen.has(link.href))
    .slice(0, 16)
    .forEach((link) => {
      seen.add(link.href);
      uniqueSources.push({ title: link.title, excerpt: `${brandName} page: ${link.title}`, href: link.href });
    });
  return uniqueSources;
}

async function ask(
  question: string,
  locale: Language,
  brandName: string,
  siteUrl: string,
  sources: BrandSearchSource[],
  signal: AbortSignal,
): Promise<{ answer: string; sources: BrandSearchSource[] }> {
  const response = await fetch('/api/ai-chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ question, locale, siteName: brandName, siteUrl, sources }),
    signal,
  });
  const payload = await response.json().catch(() => null) as { answer?: string; sources?: BrandSearchSource[]; error?: string } | null;
  if (!response.ok || !payload?.answer?.trim()) throw new Error(payload?.error || 'AI request failed');
  return {
    answer: payload?.answer || '',
    sources: Array.isArray(payload?.sources) ? payload.sources : [],
  };
}

function SourceLinks({ sources, label }: { sources: BrandSearchSource[]; label: string }) {
  if (sources.length === 0) return null;
  return (
    <div className="mt-3 border-t border-[#ecd5ac] pt-2">
      <p className="text-[10px] uppercase tracking-[.1em] text-[#8a7964]">{label}</p>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {sources.slice(0, 6).map((source) => (
          <a key={`${source.href}${source.title}`} href={source.href} target="_blank" rel="noopener noreferrer" className="inline-flex max-w-full items-center gap-1 rounded border border-[#dfd2bf] bg-white px-2 py-1 text-xs text-[#5d4d39] hover:border-[#c28a27]">
            <span className="truncate">{source.title}</span><span aria-hidden="true">↗</span>
          </a>
        ))}
      </div>
    </div>
  );
}

function KeywordResults({ results, label }: { results: BrandKeywordResult[]; label: string }) {
  if (results.length === 0) return null;
  return (
    <section className="mt-3" aria-label={label}>
      <h3 className="mb-2 px-1 text-xs font-semibold text-[#806c51]">{label}</h3>
      <div className="space-y-2">
        {results.map((result) => (
          <a key={`${result.href}${result.title}`} href={result.href} className="block rounded-xl border border-[#eadfce] bg-white px-3 py-3 hover:border-[#c28a27]">
            <span className="block text-sm font-medium text-[#5d4d39]">{result.title}</span>
            <span className="mt-1 block text-xs leading-relaxed text-[#8a7964]">{result.excerpt}</span>
          </a>
        ))}
      </div>
    </section>
  );
}

export default function BrandAiChat({ brandName, siteUrl }: Props) {
  const browserLanguage = detectLanguage('', typeof navigator === 'undefined' ? 'zh-TW' : navigator.language);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<Mode>('keyword');
  const [keyword, setKeyword] = useState('');
  const [query, setQuery] = useState('');
  const [searchAttempt, setSearchAttempt] = useState(0);
  const [answer, setAnswer] = useState('');
  const [sources, setSources] = useState<BrandSearchSource[]>([]);
  const [keywordResults, setKeywordResults] = useState<BrandKeywordResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [language, setLanguage] = useState<Language>(browserLanguage);
  const text = ui[language];

  useEffect(() => {
    let lastHref = '';
    const capture = () => {
      if (window.location.href === lastHref) return;
      lastHref = window.location.href;
      rememberCurrentPage(brandName, siteUrl);
    };
    capture();
    const timer = window.setInterval(capture, 800);
    return () => window.clearInterval(timer);
  }, [brandName, siteUrl]);

  useEffect(() => {
    if (!query) return;
    const controller = new AbortController();
    const queryLanguage = detectLanguage(query, browserLanguage);
    const pageSources = collectSources(brandName, siteUrl);
    setLanguage(queryLanguage);
    setKeywordResults(searchBrandSources(query, pageSources));
    setAnswer('');
    setSources([]);
    setError('');

    if (mode === 'keyword') {
      setLoading(false);
      return () => controller.abort();
    }

    setLoading(true);
    ask(query, queryLanguage, brandName, siteUrl, pageSources, controller.signal)
      .then((result) => {
        setAnswer(result.answer);
        setSources(result.sources);
      })
      .catch(() => {
        if (!controller.signal.aborted) setError(ui[queryLanguage].aiError);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [brandName, browserLanguage, mode, query, searchAttempt, siteUrl]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value || loading) return;
    setQuery(value);
    setSearchAttempt((attempt) => attempt + 1);
  };

  const close = () => {
    setOpen(false);
    setKeyword('');
    setQuery('');
    setAnswer('');
    setSources([]);
    setKeywordResults([]);
    setError('');
    setLoading(false);
  };

  return (
    <aside data-brand-ai-chat className="fixed bottom-24 right-5 z-[60] font-sans sm:right-6" aria-label={text.title}>
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} aria-label={text.open} aria-expanded={false} className="flex h-14 w-14 items-center justify-center rounded-full border border-white/70 bg-[#d99a2b] text-white shadow-[0_8px_24px_rgba(0,0,0,.22)] transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[#d99a2b] focus:ring-offset-2">
          <span className="text-xl" aria-hidden="true">✦</span>
        </button>
      ) : (
        <section role="dialog" aria-label={text.title} className="flex h-[min(78vh,590px)] w-[min(92vw,410px)] flex-col overflow-hidden rounded-2xl border border-[#ead6b2] bg-[#fffaf2] text-[#322719] shadow-[0_14px_42px_rgba(59,38,15,.28)]">
          <header className="flex items-center justify-between border-b border-[#eadfce] px-4 py-3">
            <div className="flex min-w-0 items-center gap-2">
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#fde9bf] text-[#a5680c]" aria-hidden="true">✦</span>
              <div className="min-w-0"><h2 className="truncate text-sm font-semibold">{text.title}</h2><p className="truncate text-[11px] text-[#8a7964]">{text.subtitle}</p></div>
            </div>
            <button type="button" onClick={close} aria-label={text.close} className="h-8 w-8 rounded-full text-lg text-[#8b806f] hover:bg-[#f2e7d4]">×</button>
          </header>

          <div className="grid grid-cols-2 gap-1 border-b border-[#eadfce] px-3 py-2" role="group" aria-label={text.title}>
            <button type="button" aria-pressed={mode === 'keyword'} onClick={() => setMode('keyword')} className={`rounded-full px-2 py-2 text-xs font-medium transition-colors ${mode === 'keyword' ? 'bg-[#d99a2b] text-white' : 'bg-white text-[#766b5c] hover:bg-[#f5ead7]'}`}>{text.keywordMode}</button>
            <button type="button" aria-pressed={mode === 'ai'} onClick={() => setMode('ai')} className={`rounded-full px-2 py-2 text-xs font-medium transition-colors ${mode === 'ai' ? 'bg-[#d99a2b] text-white' : 'bg-white text-[#766b5c] hover:bg-[#f5ead7]'}`}>{text.aiMode}</button>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3" aria-live="polite">
            {!query && <p className="rounded-xl bg-white/75 px-3 py-4 text-sm leading-relaxed text-[#645644]">{text.start}</p>}
            {query && (
              <>
                <p className="mb-3 rounded-xl bg-white/75 px-3 py-3 text-xs text-[#766b5c]">{query}</p>
                {loading && <p className="mb-3 rounded-xl border border-[#efd39c] bg-[#fff4dc] px-3 py-3 text-sm">{text.loading}</p>}
                {error && <p className="mb-3 rounded-xl border border-[#e7c7bd] bg-[#fff4f0] px-3 py-3 text-sm text-[#8e4c3c]">{error}</p>}
                {answer && <article className="rounded-xl border border-[#edc77c] bg-[#fff4dc] p-3"><div className="text-xs font-semibold text-[#9a620b]">{text.aiMode}</div><p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed">{answer}</p><SourceLinks sources={sources} label={text.sources} /></article>}
                {(mode === 'keyword' || Boolean(error)) && (
                  keywordResults.length > 0
                    ? <KeywordResults results={keywordResults} label={text.searchResults} />
                    : !loading && <p className="rounded-xl bg-white/75 px-3 py-3 text-sm text-[#766b5c]">{text.noResults}</p>
                )}
              </>
            )}
          </div>

          <form onSubmit={submit} className="flex items-center gap-2 border-t border-[#eadfce] bg-white/45 p-3">
            <label className="relative min-w-0 flex-1"><span className="sr-only">{text.placeholder}</span><input autoFocus type="search" value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder={text.placeholder} className="w-full rounded-full border border-[#dfd2bf] bg-white px-4 py-2.5 text-sm text-[#322719] outline-none placeholder:text-[#9d907e] focus:border-[#c28a27] focus:ring-2 focus:ring-[#c28a27]/20" /></label>
            <button type="submit" aria-label={text.send} disabled={!keyword.trim() || loading} className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#d99a2b] text-lg text-white transition-transform hover:scale-105 disabled:cursor-not-allowed disabled:opacity-45">➤</button>
          </form>
        </section>
      )}
    </aside>
  );
}
